import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import colors from "core/colors";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "35";
const height = "40";
const viewBox = "0 0 35 40";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({
    small: { width: 17, height: 17 },
    medium: { width: 24, height: 24 }
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            fill={colors.secondary.oolong}
            fillRule="evenodd"
            d="M8.37 39.784H6.587l-.426-1.757c-.057-.238-.055-.239-.33-.239-.805 0-1.611.003-2.417-.002-.13-.001-.182.032-.212.148-.148.571-.307 1.14-.462 1.711-.013.048-.03.095-.047.143H.967c.062-.226.117-.442.178-.656.85-3.02 1.703-6.038 2.55-9.059.034-.121.082-.16.22-.159.509.008 1.017.01 1.525 0 .158-.004.21.045.246.175.613 2.22 1.231 4.438 1.848 6.657l.796 2.865c.015.053.025.107.04.173zm-2.737-3.742l-.937-3.79h-.048l-.965 3.79h1.95zM24.026 33.353c.223-.356.472-.666.86-.864.738-.378 1.665-.192 2.255.447.475.513.725 1.117.845 1.766.22 1.187.194 2.366-.201 3.524-.167.487-.425.935-.83 1.297-.744.663-2.204.703-2.834-.409l-.127-.22v.886H22.5v-9.85h1.527v3.423zm-.098 2.726c.038.373.051.715.113 1.05.073.399.243.765.598 1.037.442.34 1.03.287 1.366-.139.123-.157.222-.338.29-.522.283-.767.288-1.551.123-2.34-.074-.35-.213-.678-.507-.935-.42-.367-1.067-.344-1.423.078a1.67 1.67 0 00-.335.61c-.107.388-.156.79-.225 1.161zM14.553 39.784h-1.485v-.815a1.31 1.31 0 01-.039-.008c-.043.07-.088.14-.13.21-.615 1.048-2.151 1.023-2.877.288-.491-.498-.75-1.095-.894-1.73-.253-1.12-.275-2.248.018-3.368.121-.467.337-.9.646-1.294.528-.672 1.435-.943 2.209-.655.39.145.656.412.865.733.05.076.095.153.172.225V29.93h1.515v9.855zm-4.017-3.713c0 .849.14 1.41.438 1.867.339.518 1.048.6 1.518.178.227-.204.35-.46.44-.729.221-.657.185-1.327.092-1.996-.054-.392-.173-.765-.45-1.085-.354-.407-1.13-.465-1.52.001-.091.11-.173.233-.236.359-.24.48-.272.994-.282 1.405zM30.24 36.65c0 .474.095.902.341 1.286.24.373.63.542 1.1.492.433-.047.683-.311.826-.668.016-.041.035-.082.051-.12h1.583c-.05.176-.084.34-.142.496-.226.613-.56 1.161-1.184 1.52-1.26.727-2.948.258-3.611-.998-.342-.65-.457-1.344-.502-2.052-.065-1.016.012-2.013.5-2.954a2.537 2.537 0 013.236-1.176c.648.265 1.037.746 1.305 1.32.3.644.42 1.32.443 2.014.009.273 0 .547 0 .84H30.24zm2.353-1.256c0-.459-.137-.865-.447-1.2a.976.976 0 00-1.406-.02c-.336.344-.41.774-.46 1.22h2.313zM21.498 36.154c.003.79-.111 1.555-.493 2.273-.39.733-.957 1.3-1.87 1.489-.971.201-1.814-.024-2.495-.696-.611-.603-.888-1.337-.998-2.13-.133-.96-.107-1.911.233-2.84.181-.496.464-.941.887-1.302a2.707 2.707 0 013.68.135c.586.586.858 1.3.975 2.067.05.332.056.67.081 1.004zm-1.597-.018c-.015-.223-.019-.448-.047-.67-.05-.393-.157-.771-.425-1.095-.484-.587-1.392-.57-1.835.044a1.967 1.967 0 00-.264.533c-.194.588-.193 1.192-.13 1.797.04.375.125.741.345 1.07a1.2 1.2 0 001.498.415c.327-.154.506-.42.628-.722.179-.443.224-.904.23-1.372zM.81 27.89V.003h12.36L.842 27.897l-.032-.008zM21.749 0h12.358v27.88a.92.92 0 00-.03.01L21.75 0zM11.374 22.763l5.75-11.912 8.255 17.07c-.089.004-.155.01-.221.01-1.6 0-3.2-.003-4.799.004-.156 0-.218-.051-.275-.182-.736-1.672-1.48-3.34-2.214-5.013-.07-.159-.16-.198-.328-.192-1.438.054-2.876.1-4.314.15-.604.02-1.207.042-1.854.065z"
            clipRule="evenodd"
            key="key-0"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "ApiCustomerAdobeLogo"
});
